import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Card, CardContent, Chip, IconButton, Link, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography, Button, Grid
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AccountBalance, ChevronLeft, ChevronRight, InfoOutlined, TrendingUp, TrendingDown } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import api from '../services/api';
import type { CashFlowResponse } from '../types';
import { OrderDetailDialog } from '../components/OrderDetailDialog';

const isoToDate = (iso: string): Date | null => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const dateToIso = (d: Date | null): string => {
  if (!d || isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtCurrency = (v: number) => `$ ${Math.round(v).toLocaleString('es-AR')}`;
// Eje de los gráficos: auto-escala la unidad (1.5M, 15M, 250k) para no tener que
// pasar el mouse por cada barra. Mantiene el signo para egresos/saldos negativos.
const fmtAxis = (v: number) => {
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toLocaleString('es-AR', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}k`;
  return `${sign}$${Math.round(abs)}`;
};
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('es-AR') : '—';

export const CashFlowPage: React.FC = () => {
  const [data, setData] = useState<CashFlowResponse | null>(null);
  const [from, setFrom] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [to, setTo] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [loading, setLoading] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [drilldownOrderId, setDrilldownOrderId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getCashFlow({ from, to });
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const navigate = useNavigate();

  const chronologicalData = useMemo(() => {
    if (!data) return [];
    const items: { date: string; description: string; ingreso: number; egreso: number; balance: number }[] = [];
    const allEntries = [
      ...data.inflows.map(i => ({ date: i.date || '', description: i.description, amount: i.amount, isInflow: true })),
      ...data.outflows.map(o => ({ date: o.date || '', description: o.description, amount: o.amount, isInflow: false })),
    ].sort((a, b) => a.date.localeCompare(b.date));
    let balance = 0;
    for (const entry of allEntries) {
      if (entry.isInflow) balance += entry.amount;
      else balance -= entry.amount;
      items.push({
        date: entry.date,
        description: entry.description,
        ingreso: entry.isInflow ? entry.amount : 0,
        egreso: entry.isInflow ? 0 : entry.amount,
        balance,
      });
    }
    return items;
  }, [data]);

  const dailyChartData = useMemo(() => {
    if (!data) return [];
    const byDate = new Map<string, { date: string; ingreso: number; egreso: number }>();
    data.inflows.forEach(i => {
      const d = i.date || 'sin-fecha';
      const cur = byDate.get(d) || { date: d, ingreso: 0, egreso: 0 };
      cur.ingreso += i.amount;
      byDate.set(d, cur);
    });
    data.outflows.forEach(o => {
      const d = o.date || 'sin-fecha';
      const cur = byDate.get(d) || { date: d, ingreso: 0, egreso: 0 };
      cur.egreso += o.amount;
      byDate.set(d, cur);
    });
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const cumulativeData = useMemo(() => {
    let balance = 0;
    return dailyChartData.map(d => {
      balance += d.ingreso - d.egreso;
      return { date: d.date, balance };
    });
  }, [dailyChartData]);

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    return dailyChartData.filter(d => {
      if (d.date === 'sin-fecha') return false;
      const dt = new Date(d.date);
      return dt.getFullYear() === year && dt.getMonth() === month;
    });
  }, [dailyChartData, calendarMonth]);

  const calendarMonthLabel = useMemo(() => {
    return calendarMonth.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
  }, [calendarMonth]);

  const handlePrevMonth = () => {
    setCalendarMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  };

  const handleNextMonth = () => {
    setCalendarMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>Flujo de Caja</Typography>
      <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
        <AlertTitle sx={{ mb: 0.5 }}>Cobros + proyecciones</AlertTitle>
        Esta vista combina <b>cobros confirmados</b>, <b>cuotas proyectadas</b> según el cronograma,
        y <b>ventas pendientes de cobro</b>. Clickeá cualquier fila de ingreso para abrir el detalle
        de la venta y verificar si efectivamente se cobró.
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <DatePicker
          label="Desde"
          value={isoToDate(from)}
          onChange={(d) => setFrom(dateToIso(d as Date | null))}
          inputFormat="dd/MM/yyyy"
          renderInput={(params) => <TextField {...params} size="small" />}
        />
        <DatePicker
          label="Hasta"
          value={isoToDate(to)}
          onChange={(d) => setTo(dateToIso(d as Date | null))}
          inputFormat="dd/MM/yyyy"
          renderInput={(params) => <TextField {...params} size="small" />}
        />
        <Button variant="contained" onClick={load} disabled={loading}>Consultar</Button>
        <Button variant="outlined" onClick={() => navigate('/calendar')}>Ver Calendario Financiero</Button>
      </Stack>

      {data && (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: (t) => alpha(t.palette.success.main, 0.06) }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TrendingUp color="success" />
                    <Typography variant="subtitle2" color="text.secondary">Ingresos</Typography>
                    <Tooltip title="Cobros confirmados: solo ventas con Abono > 0 o estado 'Pagado'. Ventas pendientes de cobro no se incluyen.">
                      <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Stack>
                  <Typography variant="h5" fontWeight={700} color="success.main">{fmtCurrency(data.totalInflows)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {data.inflows.length} {data.inflows.length === 1 ? 'cobro' : 'cobros'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.06) }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TrendingDown color="error" />
                    <Typography variant="subtitle2" color="text.secondary">Egresos</Typography>
                  </Stack>
                  <Typography variant="h5" fontWeight={700} color="error.main">{fmtCurrency(data.totalOutflows)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: (t) => alpha(data.netBalance >= 0 ? t.palette.primary.main : t.palette.warning.main, 0.06) }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccountBalance color={data.netBalance >= 0 ? 'primary' : 'warning'} />
                    <Typography variant="subtitle2" color="text.secondary">Balance Neto</Typography>
                  </Stack>
                  <Typography variant="h5" fontWeight={700} color={data.netBalance >= 0 ? 'primary.main' : 'warning.main'}>{fmtCurrency(data.netBalance)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Bar chart: Ingresos vs Egresos */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1, mt: 3 }}>Ingresos vs Egresos</Typography>
          <Card sx={{ mb: 3, p: 2 }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyChartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(d) => d !== 'sin-fecha' ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }) : '—'} />
                <YAxis width={70} tickFormatter={(v) => fmtAxis(Number(v))} />
                <RechartsTooltip formatter={(v: any) => `$ ${Math.round(Number(v) || 0).toLocaleString('es-AR')}`} />
                <Legend />
                <Bar dataKey="ingreso" name="Ingreso" fill="#16a34a" />
                <Bar dataKey="egreso" name="Egreso" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* Line chart: Saldo Acumulado */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1, mt: 3 }}>Saldo Acumulado</Typography>
          <Card sx={{ mb: 3, p: 2 }}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={cumulativeData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(d) => d !== 'sin-fecha' ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }) : '—'} />
                <YAxis width={70} tickFormatter={(v) => fmtAxis(Number(v))} />
                <RechartsTooltip formatter={(v: any) => `$ ${Math.round(Number(v) || 0).toLocaleString('es-AR')}`} />
                <Line type="monotone" dataKey="balance" name="Saldo" stroke="#2563eb" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* Combined chronological table: Movimientos */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1, mt: 3 }}>Movimientos</Typography>
          <Card sx={{ mb: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell align="right">Ingreso</TableCell>
                    <TableCell align="right">Egreso</TableCell>
                    <TableCell align="right">Saldo Acumulado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chronologicalData.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{fmtDate(row.date)}</TableCell>
                      <TableCell align="right">{row.ingreso > 0 ? <Typography color="success.main" fontWeight={600}>{fmtCurrency(row.ingreso)}</Typography> : '—'}</TableCell>
                      <TableCell align="right">{row.egreso > 0 ? <Typography color="error.main" fontWeight={600}>{fmtCurrency(row.egreso)}</Typography> : '—'}</TableCell>
                      <TableCell align="right"><Typography fontWeight={600} color={row.balance >= 0 ? 'primary.main' : 'error.main'}>{fmtCurrency(row.balance)}</Typography></TableCell>
                    </TableRow>
                  ))}
                  {chronologicalData.length === 0 && (
                    <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary" align="center">Sin movimientos</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Inflows table */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" fontWeight={600}>Ingresos (Cobros + proyecciones + pendientes)</Typography>
            <Tooltip title="Verde: ya cobrado. Naranja: cuota proyectada según cronograma. Rojo: venta pendiente de cobro. Click en la fila para abrir el detalle de la venta.">
              <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Stack>
          <Card sx={{ mb: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Monto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.inflows.map((item, i) => {
                    const isClickable = !!item.orderId;
                    const typeColor: 'success' | 'warning' | 'error' | 'default' =
                      item.type === 'Cobro venta' || item.type === 'Cobro oportunidad' ? 'success'
                      : item.type === 'Cuota proyectada' ? 'warning'
                      : item.type === 'Pendiente cobro' ? 'error'
                      : 'default';
                    const amountColor =
                      item.type === 'Pendiente cobro' ? 'error.main'
                      : item.type === 'Cuota proyectada' ? 'warning.main'
                      : 'success.main';
                    return (
                      <TableRow
                        key={i}
                        hover={isClickable}
                        sx={isClickable ? { cursor: 'pointer' } : undefined}
                        onClick={() => isClickable && setDrilldownOrderId(item.orderId!)}
                      >
                        <TableCell>{fmtDate(item.date)}</TableCell>
                        <TableCell><Chip size="small" color={typeColor} label={item.type} variant="outlined" /></TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.customerName || '—'}</TableCell>
                        <TableCell align="right"><Typography color={amountColor} fontWeight={600}>{fmtCurrency(item.amount)}</Typography></TableCell>
                      </TableRow>
                    );
                  })}
                  {data.inflows.length === 0 && (
                    <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary" align="center">Sin ingresos en el período</Typography></TableCell></TableRow>
                  )}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={4}><Typography fontWeight={600}>Total (incluye proyectados y pendientes)</Typography></TableCell>
                    <TableCell align="right"><Typography fontWeight={700} color="success.main">{fmtCurrency(data.totalInflows)}</Typography></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Outflows table */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Egresos (Compras MP/Insumos)</Typography>
          <Card>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="right">Monto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.outflows.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{fmtDate(item.date)}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.type}</TableCell>
                      <TableCell align="right"><Typography color="error.main" fontWeight={600}>{fmtCurrency(item.amount)}</Typography></TableCell>
                    </TableRow>
                  ))}
                  {data.outflows.length === 0 && (
                    <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary" align="center">Sin egresos en el período</Typography></TableCell></TableRow>
                  )}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={3}><Typography fontWeight={600}>Total Egresos</Typography></TableCell>
                    <TableCell align="right"><Typography fontWeight={700} color="error.main">{fmtCurrency(data.totalOutflows)}</Typography></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Financial Calendar View */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1, mt: 3 }}>Calendario Financiero</Typography>
          <Card sx={{ mb: 3, p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 2 }}>
              <IconButton onClick={handlePrevMonth} size="small"><ChevronLeft /></IconButton>
              <Typography variant="h6" fontWeight={600} sx={{ textTransform: 'capitalize', minWidth: 200, textAlign: 'center' }}>
                {calendarMonthLabel}
              </Typography>
              <IconButton onClick={handleNextMonth} size="small"><ChevronRight /></IconButton>
            </Stack>
            {calendarDays.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                Sin movimientos en este mes
              </Typography>
            ) : (
              <Grid container spacing={1.5}>
                {calendarDays.map((day) => {
                  const dt = new Date(day.date);
                  const dayNum = dt.getDate();
                  const weekday = dt.toLocaleDateString('es-AR', { weekday: 'short' });
                  return (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={day.date}>
                      <Box sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 1.5,
                        minHeight: 80,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {weekday}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                          {dayNum}
                        </Typography>
                        <Stack direction="column" spacing={0.5}>
                          {day.ingreso > 0 && (
                            <Chip
                              label={`+${fmtCurrency(day.ingreso)}`}
                              size="small"
                              sx={{
                                bgcolor: '#E8F5E9',
                                color: '#2e7d32',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          )}
                          {day.egreso > 0 && (
                            <Chip
                              label={`-${fmtCurrency(day.egreso)}`}
                              size="small"
                              sx={{
                                bgcolor: '#FFEBEE',
                                color: '#c62828',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Card>
        </>
      )}

      <OrderDetailDialog
        orderId={drilldownOrderId}
        open={drilldownOrderId != null}
        onClose={() => setDrilldownOrderId(null)}
      />
    </Box>
  );
};

export default CashFlowPage;
